import { skipToken } from "@reduxjs/toolkit/query";
import {
	Calendar,
	ChevronLeft,
	ChevronRight,
	Clock,
	MessageCircle,
	Plus,
	Search,
	User,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import Input from "../../components/ui/Input";
import Loader from "../../components/ui/Loader";
import Select from "../../components/ui/Select";
import { useToast } from "../../contexts/ToastContext";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useListPropertiesQuery } from "../../store/api/propertiesApi";
import {
	useCancelViewingMutation,
	useCompleteViewingMutation,
	useConfirmViewingMutation,
	useCreateViewingMutation,
	useDeleteViewingMutation,
	useListViewingsQuery,
	useUpdateViewingMutation,
} from "../../store/api/propertyViewingsApi";
import { useListUsersQuery } from "../../store/api/usersApi";
import BuyerProfileModal from "./components/BuyerProfileModal";
import ScheduleViewingModal from "./components/ScheduleViewingModal";
import ViewingCalendar from "./components/ViewingCalendar";
import ViewingRequests from "./components/ViewingRequests";

const PropertyViewingManagement = () => {
	const { success, error: showError } = useToast();
	const navigate = useNavigate();
	// Get user profile
	const { data: userProfile } = useGetProfileQuery();

	// State management
	const [selectedViewing, setSelectedViewing] = useState<any>(null);
	const [selectedBuyer, setSelectedBuyer] = useState<any>(null);
	const [showScheduleModal, setShowScheduleModal] = useState(false);
	const [showBuyerProfile, setShowBuyerProfile] = useState(false);
	const [viewType, setViewType] = useState<"calendar" | "requests">("calendar");
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "all",
		dateRange: "week",
		search: "",
	});
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [viewingToDelete, setViewingToDelete] = useState<any>(null);

	// Fetch viewings from API
	// Always fetch all viewings (no status filter) so Requests tab can show pending viewings
	// Status filtering is done client-side for the calendar view
	const {
		data: viewingsData,
		isLoading: isLoadingViewings,
		refetch: refetchViewings,
	} = useListViewingsQuery({
		page,
		page_size: 20, // Paginated - 20 viewings per page
		// Don't filter by status here - we need all viewings for both tabs
		// Status filter is applied client-side for calendar view only
		search: filters.search || undefined,
	});

	// Fetch properties for the modal
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 100 });

	// Fetch agents for the modal (only for admins - agents can't access users endpoint)
	const isAdmin = userProfile?.role === "admin";
	const { data: agentsData } = useListUsersQuery(
		isAdmin
			? {
					role: "agent",
					page_size: 100,
			  }
			: skipToken,
	);

	// Mutations
	const [createViewing] = useCreateViewingMutation();
	const [updateViewing] = useUpdateViewingMutation();
	const [deleteViewing] = useDeleteViewingMutation();
	const [confirmViewing] = useConfirmViewingMutation();
	const [cancelViewing] = useCancelViewingMutation();
	const [completeViewing] = useCompleteViewingMutation();

	// Get all viewings from API (unfiltered)
	const allViewings = useMemo(() => {
		if (!viewingsData?.results) return [];
		// Backend already provides all fields in snake_case
		return viewingsData.results;
	}, [viewingsData]);

	// Filtered viewings - apply status filter client-side for both Calendar and Requests tabs
	// "All Status" shows all viewings, other statuses filter accordingly
	const viewings = useMemo(() => {
		// Apply status filter when status is not "all"
		if (filters.status !== "all") {
			return allViewings.filter((v) => {
				const status =
					typeof v.status === "string"
						? v.status.toLowerCase()
						: String(v.status || "").toLowerCase();
				return status === filters.status.toLowerCase();
			});
		}

		// Show all viewings when status is "all"
		return allViewings;
	}, [allViewings, filters.status]);

	// Viewing requests - use same filtered viewings as Calendar tab
	// Users can filter by status using the status dropdown
	const viewingRequests = viewings;

	// Count of pending viewings (for tab badge) - always count from all viewings, not filtered
	const pendingViewingsCount = useMemo(() => {
		return allViewings.filter((v) => {
			const status =
				typeof v.status === "string"
					? v.status.toLowerCase()
					: String(v.status || "").toLowerCase();
			return status === "pending";
		}).length;
	}, [allViewings]);

	const handleScheduleViewing = async (viewingData: any) => {
		try {
			// Combine date and time into ISO datetime string
			const viewingDateTime = new Date(
				`${viewingData.date}T${viewingData.time || "09:00"}`,
			).toISOString();

			// For agents, automatically assign the viewing to themselves if no agent is specified
			const agentId =
				viewingData.agentId ||
				viewingData.agent ||
				(!isAdmin && userProfile?.id ? userProfile.id : "");

			const payload = {
				// Organization is optional for in-house app
				property: viewingData.property,
				buyer_name: viewingData.buyer_name,
				buyer_email: viewingData.buyer_email || "",
				buyer_phone: viewingData.buyer_phone || "",
				viewing_date: viewingDateTime,
				duration: viewingData.duration || 30,
				status: viewingData.status || "scheduled",
				notes: viewingData.notes || "",
				agent: agentId,
			};

			if (selectedViewing) {
				// Update existing viewing (exclude organization from update payload)
				const updatePayload = {
					property: payload.property,
					buyer_name: payload.buyer_name,
					buyer_email: payload.buyer_email,
					buyer_phone: payload.buyer_phone,
					viewing_date: payload.viewing_date,
					duration: payload.duration,
					status: payload.status,
					notes: payload.notes,
					agent: payload.agent,
				};
				await updateViewing({
					id: selectedViewing.id,
					data: updatePayload,
				}).unwrap();
				success(
					"Viewing rescheduled successfully! The buyer will be notified by email.",
				);
			} else {
				// Create new viewing
				await createViewing(payload).unwrap();
				success("Viewing scheduled successfully!");
			}

			setShowScheduleModal(false);
			setSelectedViewing(null);
			refetchViewings();
		} catch (error: any) {
			console.error("Failed to save viewing:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to save viewing. Please try again.",
			);
		}
	};

	const handleViewingAction = async (viewing: any, action: string) => {
		try {
			switch (action) {
				case "reschedule":
					setSelectedViewing(viewing);
					setShowScheduleModal(true);
					break;
				case "confirm":
					await confirmViewing(viewing.id).unwrap();
					success("Viewing confirmed successfully!");
					refetchViewings();
					break;
				case "cancel":
					await cancelViewing(viewing.id).unwrap();
					success("Viewing cancelled successfully!");
					refetchViewings();
					break;
				case "complete":
					await completeViewing(viewing.id).unwrap();
					success("Viewing marked as completed!");
					refetchViewings();
					break;
				case "delete":
					setViewingToDelete(viewing);
					setIsDeleteModalOpen(true);
					break;
				case "chat":
					// Navigate to messaging center with buyer email as query param
					if (viewing.buyer_email) {
						navigate(
							`/client-messaging-center?email=${encodeURIComponent(
								viewing.buyer_email,
							)}`,
						);
					} else {
						navigate("/client-messaging-center");
					}
					break;
				default:
					break;
			}
		} catch (error: any) {
			console.error(`Failed to ${action} viewing:`, error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					`Failed to ${action} viewing. Please try again.`,
			);
		}
	};

	const handleConfirmDelete = async () => {
		if (!viewingToDelete) return;

		try {
			await deleteViewing(viewingToDelete.id).unwrap();
			success("Viewing deleted successfully!");
			setIsDeleteModalOpen(false);
			setViewingToDelete(null);
			refetchViewings();
		} catch (error: any) {
			console.error("Failed to delete viewing:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to delete viewing. Please try again.",
			);
		}
	};

	const handleRequestAction = async (request: any, action: string) => {
		try {
			switch (action) {
				case "approve":
					// Convert pending request to scheduled viewing
					// Backend sends viewing_date as ISO datetime string
					const viewingDateTime =
						request.viewing_date || new Date().toISOString();

					if (request.id) {
						// Update existing pending viewing to scheduled
						await updateViewing({
							id: request.id,
							data: { status: "scheduled" },
						}).unwrap();
						success("Viewing request approved and scheduled!");
					} else {
						// Create new viewing from request (organization is optional for in-house app)
						await createViewing({
							property: request.property,
							buyer_name: request.buyer_name,
							buyer_email: request.buyer_email || "",
							buyer_phone: request.buyer_phone || "",
							viewing_date: viewingDateTime,
							duration: request.duration || 30,
							status: "scheduled",
							notes: request.notes || "",
						}).unwrap();
						success("Viewing request approved and scheduled!");
					}
					refetchViewings();
					break;
				case "reject":
					if (request.id) {
						await cancelViewing(request.id).unwrap();
						success("Viewing request rejected.");
						refetchViewings();
					}
					break;
				case "profile":
					setSelectedBuyer(request);
					setShowBuyerProfile(true);
					break;
				case "chat":
					// Navigate to messaging center with buyer email as query param
					if (request.buyer_email) {
						navigate(
							`/client-messaging-center?email=${encodeURIComponent(
								request.buyer_email,
							)}`,
						);
					} else {
						navigate("/client-messaging-center");
					}
					break;
				default:
					break;
			}
		} catch (error: any) {
			console.error(`Failed to ${action} request:`, error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					`Failed to ${action} request. Please try again.`,
			);
		}
	};

	// Loading state
	if (isLoadingViewings) {
		return (
			<Loader size="lg" showText text="Loading viewings..." className="h-64" />
		);
	}

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Header Actions */}
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
					<div className="min-w-0 flex-1">
						<h1 className="text-2xl font-bold text-foreground truncate">
							Viewing Management
						</h1>
						<p className="text-muted-foreground break-words">
							Schedule and coordinate property viewings with potential buyers
						</p>
					</div>

					<div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0">
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate("/client-messaging-center")}
							className="flex items-center gap-2 w-full sm:w-auto"
						>
							<MessageCircle size={16} />
							<span className="truncate">Messages</span>
						</Button>
						<Button
							onClick={() => setShowScheduleModal(true)}
							size="sm"
							className="flex items-center gap-2 w-full sm:w-auto"
						>
							<Plus size={16} />
							<span className="truncate">Schedule</span>
						</Button>
					</div>
				</div>

				{/* View Toggle and Filters */}
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
					<div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1 overflow-hidden">
						<Button
							variant={viewType === "calendar" ? "default" : "ghost"}
							size="sm"
							onClick={() => setViewType("calendar")}
							className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0"
						>
							<Calendar size={16} className="flex-shrink-0" />
							<span className="truncate">Calendar View</span>
						</Button>
						<Button
							variant={viewType === "requests" ? "default" : "ghost"}
							size="sm"
							onClick={() => setViewType("requests")}
							className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0"
						>
							<Clock size={16} className="flex-shrink-0" />
							<span className="truncate">Requests</span>
							{pendingViewingsCount > 0 && (
								<span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5 ml-1">
									{pendingViewingsCount}
								</span>
							)}
						</Button>
					</div>

					<div className="flex items-center gap-3">
						<div className="relative">
							<Search
								className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
								size={16}
							/>
							<Input
								placeholder="Search viewings..."
								value={filters?.search}
								onChange={(e) => {
									setFilters((prev) => ({
										...prev,
										search: e?.target?.value,
									}));
									setPage(1); // Reset to first page when search changes
								}}
								className="pl-10 w-64"
							/>
						</div>

						<Select
							value={filters?.status}
							onValueChange={(value) => {
								setFilters((prev) => ({ ...prev, status: value as string }));
								setPage(1); // Reset to first page when status filter changes
							}}
							options={[
								{ value: "all", label: "All Status" },
								{ value: "pending", label: "Pending" },
								{ value: "scheduled", label: "Scheduled" },
								{ value: "confirmed", label: "Confirmed" },
								{ value: "completed", label: "Completed" },
								{ value: "cancelled", label: "Cancelled" },
							]}
						/>
					</div>
				</div>

				{/* Statistics Cards */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					{[
						{
							title: "Total Viewings",
							value: allViewings.length,
							icon: Calendar,
							color: "text-blue-600",
							bgColor: "bg-blue-50 dark:bg-blue-950/20",
						},
						{
							title: "Pending Requests",
							value: allViewings.filter((v) => {
								const status =
									typeof v.status === "string"
										? v.status.toLowerCase()
										: String(v.status || "").toLowerCase();
								return status === "pending";
							}).length,
							icon: Clock,
							color: "text-orange-600",
							bgColor: "bg-orange-50 dark:bg-orange-950/20",
						},
						{
							title: "Confirmed Today",
							value: allViewings.filter((v) => {
								const status =
									typeof v.status === "string"
										? v.status.toLowerCase()
										: String(v.status || "").toLowerCase();
								if (status !== "confirmed") return false;
								const viewingDate = new Date(v.viewing_date);
								const today = new Date();
								return viewingDate.toDateString() === today.toDateString();
							}).length,
							icon: User,
							color: "text-green-600",
							bgColor: "bg-green-50 dark:bg-green-950/20",
						},
						{
							title: "Completed This Week",
							value: allViewings.filter((v) => {
								const status =
									typeof v.status === "string"
										? v.status.toLowerCase()
										: String(v.status || "").toLowerCase();
								return status === "completed";
							}).length,
							icon: MessageCircle,
							color: "text-purple-600",
							bgColor: "bg-purple-50 dark:bg-purple-950/20",
						},
					].map((stat, index) => {
						const Icon = stat?.icon;
						return (
							<div
								key={index}
								className="bg-card border border-border rounded-lg p-6 overflow-hidden"
							>
								<div className="flex items-center justify-between">
									<div className="flex-1">
										<p className="text-sm font-medium text-muted-foreground mb-1">
											{stat?.title}
										</p>
										<p className="text-2xl font-bold text-foreground">
											{stat?.value}
										</p>
									</div>
									<div className={`${stat?.bgColor} p-3 rounded-lg`}>
										<Icon className={`w-6 h-6 ${stat?.color}`} />
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{/* Main Content */}
				<div className="bg-card rounded-lg border border-border overflow-hidden">
					{viewType === "calendar" ? (
						<ViewingCalendar
							viewings={viewings}
							onViewingAction={handleViewingAction}
							filters={filters}
						/>
					) : (
						<ViewingRequests
							requests={viewingRequests}
							onRequestAction={handleRequestAction}
							filters={filters}
						/>
					)}
				</div>

				{/* Pagination */}
				{viewingsData &&
					viewingsData.count > 0 &&
					(viewingsData.next || viewingsData.previous) && (
						<div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
							<p className="text-sm text-muted-foreground">
								Showing {(page - 1) * 20 + 1} to{" "}
								{Math.min(page * 20, viewingsData.count)} of{" "}
								{viewingsData.count} viewings
							</p>
							<div className="flex items-center space-x-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={page === 1 || isLoadingViewings}
									className="flex items-center gap-2"
								>
									<ChevronLeft size={16} />
									<span className="hidden sm:inline">Previous</span>
									<span className="sm:hidden">Prev</span>
								</Button>
								<span className="text-sm text-muted-foreground whitespace-nowrap">
									Page {page} of {Math.ceil(viewingsData.count / 20)}
								</span>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => p + 1)}
									disabled={
										!viewingsData.next ||
										isLoadingViewings ||
										page * 20 >= viewingsData.count
									}
									className="flex items-center gap-2"
								>
									Next
									<ChevronRight size={16} />
								</Button>
							</div>
						</div>
					)}

				{/* Empty State */}
				{allViewings.length === 0 && (
					<div className="text-center py-12">
						<div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
							<Calendar className="w-8 h-8 text-muted-foreground" />
						</div>
						<h3 className="text-lg font-semibold text-foreground mb-2">
							No Viewings Scheduled
						</h3>
						<p className="text-muted-foreground mb-6 max-w-md mx-auto">
							Start managing property viewings by scheduling appointments with
							potential buyers.
						</p>
						<Button
							onClick={() => setShowScheduleModal(true)}
							className="flex items-center gap-2"
						>
							<Plus size={16} />
							Schedule
						</Button>
					</div>
				)}
			</div>

			{/* Modals */}
			{showScheduleModal && (
				<ScheduleViewingModal
					viewing={selectedViewing}
					properties={propertiesData?.results || []}
					agents={
						isAdmin
							? agentsData?.results || []
							: userProfile
							? [
									{
										id: userProfile.id,
										first_name: userProfile.first_name,
										last_name: userProfile.last_name,
										email: userProfile.email,
									},
							  ]
							: []
					}
					onClose={() => {
						setShowScheduleModal(false);
						setSelectedViewing(null);
					}}
					onSave={handleScheduleViewing}
				/>
			)}
			{showBuyerProfile && selectedBuyer && (
				<BuyerProfileModal
					buyer={selectedBuyer}
					onClose={() => {
						setShowBuyerProfile(false);
						setSelectedBuyer(null);
					}}
				/>
			)}
			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setViewingToDelete(null);
				}}
				onConfirm={handleConfirmDelete}
				title="Delete Viewing"
				message={`Are you sure you want to delete this viewing for ${
					viewingToDelete?.buyer_name || "this buyer"
				}? This action cannot be undone.`}
				confirmButtonText="Delete"
				isDestructive={true}
			/>
		</div>
	);
};

export default PropertyViewingManagement;
